/**
 * PersonaSelectorModal Component
 * Allows users to change their focus/motivation
 */
import { useState } from 'react';
import { 
  GraduationCap, 
  Wallet, 
  Globe, 
  TrendingUp,
  Sparkles,
  CheckCircle2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const PERSONAS = [
  {
    id: 'course-known',
    name: 'I Know My Course',
    tagline: 'Help me find the right university',
    icon: GraduationCap,
    gradient: 'from-ux-cyan to-ux-blue'
  },
  {
    id: 'budget-clarity',
    name: 'I Need Budget Clarity',
    tagline: 'Show me affordable options',
    icon: Wallet,
    gradient: 'from-emerald-400 to-teal-500'
  },
  {
    id: 'country-help',
    name: 'Help Me Choose a Country',
    tagline: 'Compare destinations side-by-side',
    icon: Globe,
    gradient: 'from-ux-violet to-ux-rose'
  },
  {
    id: 'career-focus',
    name: 'I Care About Career Outcomes',
    tagline: 'Jobs, PR & future growth',
    icon: TrendingUp,
    gradient: 'from-amber-400 to-orange-500'
  },
  {
    id: 'ai-guided',
    name: 'Let AI Guide Me',
    tagline: 'I need full guidance',
    icon: Sparkles,
    gradient: 'from-ux-cyan to-ux-violet'
  }
];

const PersonaSelectorModal = ({ 
  isOpen, 
  onClose, 
  currentPersona,
  userId,
  onPersonaChange,
  isDark = false
}) => {
  const [selectedPersona, setSelectedPersona] = useState(currentPersona || 'ai-guided');
  const [isSaving, setIsSaving] = useState(false);
  
  const handleSave = async () => {
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/update-motivation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          user_id: userId,
          primary_motivation: selectedPersona 
        })
      });
      
      if (!response.ok) throw new Error('Failed to update');
      
      // Clear dismissed banner so new persona banner shows
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('persona_banner_dismissed_')) {
          localStorage.removeItem(key);
        }
      });
      
      toast.success('Your focus has been updated!');
      if (onPersonaChange) onPersonaChange(selectedPersona);
      onClose();
    } catch (error) {
      console.error('Error updating persona:', error);
      toast.error('Failed to update. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-lg ${isDark ? 'bg-[#233842] border-white/10' : ''}`}>
        <DialogHeader>
          <DialogTitle className={isDark ? 'text-white' : ''}>
            What's Your Priority?
          </DialogTitle>
          <DialogDescription className={isDark ? 'text-white/60' : ''}>
            Choose what matters most to you. We'll personalize your experience accordingly.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 py-4">
          {PERSONAS.map((persona) => {
            const Icon = persona.icon;
            const isSelected = selectedPersona === persona.id;
            
            return (
              <button
                key={persona.id}
                onClick={() => setSelectedPersona(persona.id)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all ${
                  isSelected 
                    ? `bg-gradient-to-r ${persona.gradient} text-white shadow-lg scale-[1.02]`
                    : isDark 
                      ? 'bg-white/5 hover:bg-white/10 border border-white/10' 
                      : 'bg-slate-50 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  isSelected ? 'bg-white/20' : `bg-gradient-to-br ${persona.gradient}`
                }`}>
                  <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-white'}`} />
                </div>
                <div className="flex-1 text-left">
                  <h4 className={`font-semibold ${
                    isSelected ? 'text-white' : isDark ? 'text-white' : 'text-ux-charcoal'
                  }`}>
                    {persona.name}
                  </h4>
                  <p className={`text-sm ${
                    isSelected ? 'text-white/80' : isDark ? 'text-white/60' : 'text-ux-charcoal/60'
                  }`}>
                    {persona.tagline}
                  </p>
                </div>
                {isSelected && (
                  <CheckCircle2 className="w-6 h-6 text-white" />
                )}
              </button>
            );
          })}
        </div>
        
        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={isSaving || selectedPersona === currentPersona}
            className="flex-1 bg-gradient-to-r from-ux-cyan to-ux-violet text-white"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PersonaSelectorModal;
